// frontend/src/components/layout/Layout.jsx
// LAYOUT PRINCIPAL QUE REPLICA base_sistema.html
import React, { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import CintaOpciones from './CintaOpciones';
import { useTheme } from '../../styles/ThemeProvider';
import { useAuth } from '../../hooks/useAuth';
import authService from '../../services/authService';
import { clearAuthSession } from '../../services/tenantSession';
import { CURRENT_VERSION } from '../../data/changelog';
import ModalCambiarPassword from '../common/ModalCambiarPassword';

// CONTENEDOR PRINCIPAL DEL LAYOUT
const LayoutContainer = styled.div`
  height: calc(100vh / var(--app-zoom, 1)); /* Compensar zoom automático */
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.bgPrincipal};
  color: ${props => props.theme.colors.txtPrincipal};
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  overflow: hidden; /* Evitar scroll de página completa */

  /* En móvil Y tablet usar dvh para descontar la barra del navegador, así el
     footer (que vive en este contenedor) llega hasta el fondo. Ver GlobalStyles. */
  @media (max-width: 1024px) {
    height: 100dvh;
  }

  @media print {
    height: auto !important;
    min-height: auto !important;
    overflow: visible !important;
    display: block;
  }
`;

// HEADER PRINCIPAL (REPLICA EL NAVBAR DE base_sistema.html)
const Header = styled.header`
  flex-shrink: 0; /* No se comprime */
  height: ${props => props.theme.heights.navbar};
  background-color: ${props => props.theme.colors.celeste};
  color: ${({ theme }) => theme.colors.onPrimary};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem;
  z-index: ${props => props.theme.zIndex.fixed};
  box-shadow: ${props => props.theme.shadows.md};

  /* Ocultar header al imprimir */
  @media print {
    display: none !important;
  }
`;

// CONTENEDOR IZQUIERDO DEL HEADER (TÍTULO)
const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

// TÍTULO DEL SISTEMA
const SystemTitle = styled.h1`
  font-size: 1.25rem;
  font-weight: bold;
  margin: 0;
  color: ${({ theme }) => theme.colors.onPrimary};
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
`;

const VersionInlineLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  color: ${({ theme }) => theme.colors.onPrimary};
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  opacity: 0.85;
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.amarillo};
    opacity: 1;
  }
`;

// CONTENEDOR CENTRO DEL HEADER (LOGO)
const HeaderCenter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
`;

// LOGO DE LA EMPRESA (más compacto)
const Logo = styled.img`
  height: 28px;
  width: auto;
  object-fit: contain;
`;

// CONTENEDOR DERECHO DEL HEADER (CONTROLES)
const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  justify-content: flex-end;

  /* En móvil se oculta y se muestra el botón de menú */
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

// BOTÓN HAMBURGUESA SÓLO EN MÓVIL
const MobileMenuToggle = styled.button`
  display: none;
  background: ${({ theme }) => theme.colors.transparent};
  border: 1px solid ${({ theme }) => theme.colors.overlayOnPrimaryBorder};
  color: ${({ theme }) => theme.colors.onPrimary};
  padding: 0.35rem 0.5rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.overlayOnPrimarySoft};
  }

  i {
    font-size: 1.1rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
    align-items: center;
  }
`;

// PANEL DESPLEGABLE DE CONTROLES EN MÓVIL
const MobileControlsPanel = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: ${({ $open }) => ($open ? 'flex' : 'none')};
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.4rem;
    padding: 0.4rem 0.75rem;
    background-color: ${({ theme }) => theme.colors.celeste};
    border-top: 1px solid ${({ theme }) => theme.colors.overlayOnPrimaryBorder};
    box-shadow: ${({ theme }) => theme.shadows.md};
    z-index: ${({ theme }) => theme.zIndex.fixed};
    flex-shrink: 0;
  }

  @media print {
    display: none !important;
  }
`;

// BOTÓN DE ALTERNANCIA DE TEMA Y LOGOUT
const HeaderButton = styled.button`
  background: ${({ theme }) => theme.colors.transparent};
  border: 1px solid ${({ theme }) => theme.colors.overlayOnPrimaryBorder};
  color: ${({ theme }) => theme.colors.onPrimary};
  padding: 0.35rem 0.45rem;
  border-radius: ${props => props.theme.borderRadius.sm};
  cursor: pointer;
  transition: ${props => props.theme.transitions.fast};
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.78rem;

  &:hover {
    background: ${({ theme }) => theme.colors.overlayOnPrimarySoft};
    border-color: ${props => props.theme.colors.amarillo};
  }

  &.logout-btn:hover {
    background: ${({ theme }) => `${theme.colors.danger}2E`};
    border-color: ${({ theme }) => theme.colors.danger};
  }

  i {
    font-size: 0.9rem;
  }
`;

// INDICADOR DE TENANT ACTUAL
const TenantBadge = styled.div`
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
  border-radius: ${props => props.theme.borderRadius.sm};
  background: ${({ theme }) => `${theme.colors.info}33`};
  border: 1px solid ${({ theme }) => theme.colors.info};
  color: ${({ theme }) => theme.colors.onPrimary};
  margin-right: 0.75rem;
  white-space: nowrap;
  font-weight: 500;
`;

// CONTENIDO PRINCIPAL
const MainContent = styled.main`
  flex: 1; /* Toma el espacio restante */
  display: flex;
  flex-direction: column;
  overflow: hidden; /* Evitar scroll en main */
  min-height: 0; /* Permitir que los hijos controlen su altura */

  @media print {
    overflow: visible !important;
    height: auto !important;
    min-height: auto !important;
    display: block;
  }
`;

// FOOTER FIJO
const Footer = styled.footer`
  flex-shrink: 0; /* No se comprime */
  background-color: ${({ theme }) => theme.colors.overlayBackdropStrong};
  color: ${({ theme }) => theme.colors.onPrimary};
  text-align: center;
  padding: 0.15rem 0.25rem; /* aún más compacto */
  font-size: 0.7rem; /* más pequeño para ahorrar espacio */

  /* Ocultar footer al imprimir */
  @media print {
    display: none !important;
  }
`;

const Layout = () => {
  const { isDarkMode, toggleTheme } = useTheme();
  const { tenantSlug, tenantContext, logout } = useAuth();
  const navigate = useNavigate();
  const [showCambiarPassword, setShowCambiarPassword] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Obtener información del usuario actual
  const currentUser = authService.getCurrentUser();
  
  // Manejar logout de manera tenant-aware
  const handleLogout = async () => {
    try {
      // Usar logout del contexto de auth para limpiar correctamente
      await logout();
      // Redirigir al login después de limpiar sesión
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error al hacer logout:', error);
      // Fallback: limpiar sesión manualmente y redirigir
      clearAuthSession();
      navigate('/login', { replace: true });
    }
  };
  
  // Logo fijo del programa FC Cloud
  const logoSrc = isDarkMode ? '/img/logo_fccloud_blanco.png' : '/img/logo_fccloud.png';

  return (
    <LayoutContainer>
      {/* HEADER PRINCIPAL CON TÍTULO, LOGO Y CONTROLES */}
      <Header>
        {/* TÍTULO A LA IZQUIERDA */}
        <HeaderLeft>
          <SystemTitle>
            <span>FC Cloud</span>
            <VersionInlineLink onClick={() => navigate('/changelog')} title="Ver historial de versiones">
              {CURRENT_VERSION}
            </VersionInlineLink>
          </SystemTitle>
        </HeaderLeft>

        {/* LOGO AL CENTRO */}
        <HeaderCenter>
          <Logo 
            src={logoSrc} 
            alt="FC Cloud Logo"
            onError={(e) => {
              // Fallback si no encuentra el logo
              e.target.style.display = 'none';
            }}
          />
        </HeaderCenter>

        {/* CONTROLES A LA DERECHA (escritorio) */}
        <HeaderRight>
          {/* Información del usuario */}
          {currentUser && (
            <span style={{ fontSize: '0.875rem', marginRight: '1rem' }}>
              👋 Hola, <strong>{currentUser.first_name || currentUser.username}</strong>
            </span>
          )}

          {/* Indicador de tenant actual */}
          {tenantSlug && (
            <TenantBadge title={`Host actual: ${tenantContext?.frontendHost || 'desconocido'}`}>
              📍 {tenantSlug}
            </TenantBadge>
          )}

          <HeaderButton onClick={toggleTheme} title="Cambiar tema">
            <i className={isDarkMode ? 'bi bi-sun-fill' : 'bi bi-moon-fill'}></i>
            <span>{isDarkMode ? 'Claro' : 'Oscuro'}</span>
          </HeaderButton>

          <HeaderButton onClick={() => setShowCambiarPassword(true)} title="Cambiar contraseña">
            <i className="bi bi-key"></i>
            <span>Contraseña</span>
          </HeaderButton>

          <HeaderButton className="logout-btn" onClick={handleLogout} title="Cerrar sesión">
            <i className="bi bi-box-arrow-right"></i>
            <span>Salir</span>
          </HeaderButton>
        </HeaderRight>

        {/* BOTÓN HAMBURGUESA (sólo en móvil) */}
        <MobileMenuToggle
          onClick={() => setMobileMenuOpen(prev => !prev)}
          title="Menú de usuario"
        >
          <i className={mobileMenuOpen ? 'bi bi-x-lg' : 'bi bi-three-dots-vertical'}></i>
        </MobileMenuToggle>
      </Header>

      {/* PANEL DE CONTROLES COLAPSABLE EN MÓVIL */}
      <MobileControlsPanel $open={mobileMenuOpen}>
        {currentUser && (
          <span style={{ fontSize: '0.8rem', color: 'inherit', flexShrink: 0 }}>
            👋 <strong>{currentUser.first_name || currentUser.username}</strong>
          </span>
        )}
        {tenantSlug && (
          <TenantBadge title={`Host actual: ${tenantContext?.frontendHost || 'desconocido'}`}>
            📍 {tenantSlug}
          </TenantBadge>
        )}
        <HeaderButton onClick={toggleTheme} title="Cambiar tema">
          <i className={isDarkMode ? 'bi bi-sun-fill' : 'bi bi-moon-fill'}></i>
          <span>{isDarkMode ? 'Claro' : 'Oscuro'}</span>
        </HeaderButton>
        <HeaderButton onClick={() => { setShowCambiarPassword(true); setMobileMenuOpen(false); }} title="Cambiar contraseña">
          <i className="bi bi-key"></i>
          <span>Contraseña</span>
        </HeaderButton>
        <HeaderButton className="logout-btn" onClick={handleLogout} title="Cerrar sesión">
          <i className="bi bi-box-arrow-right"></i>
          <span>Salir</span>
        </HeaderButton>
      </MobileControlsPanel>

      {/* CONTENIDO PRINCIPAL CON CINTA DE OPCIONES Y OUTLETS */}
      <MainContent>
        {/* LA CINTA DE OPCIONES SE INCLUYE AQUÍ COMO EN EL HTML ORIGINAL */}
        <CintaOpciones />
        
        {/* CONTENEDOR PARA LAS PÁGINAS CON ALTURA CORRECTA */}
        <div style={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          minHeight: 0,
          padding: '0', /* Eliminar padding completamente */
          overflow: 'hidden'
        }}>
          {/* AQUÍ SE RENDERIZAN LAS PÁGINAS */}
          <Outlet />
        </div>
      </MainContent>

      {/* FOOTER FIJO */}
      <Footer>
        Developed by <strong>Alejandro Iane</strong> | Feet Control Cloud
      </Footer>

      <ModalCambiarPassword
        isOpen={showCambiarPassword}
        onClose={() => setShowCambiarPassword(false)}
      />
    </LayoutContainer>
  );
};

export default Layout;
